import { useRef, useState } from 'react';
import { apiUrl, resolveMediaUrl } from '@/lib/api';
import { getSessionExpiredLoginUrl } from '@/lib/loginPath';
import { dispatchMediaQuotaBlocked } from '@/lib/mediaStorage';
import { useMediaStorage } from '@/components/MediaStorageProvider';
import { Button } from '@/components/ui/Button';
import { labelClass } from '@/components/ui/inputStyles';

export const MANUAL_ENTRY_IMAGE_MAX_BYTES = 1024 * 1024;
export const MANUAL_ENTRY_IMAGE_MAX_COUNT = 9;

type Props = {
  siteId: number;
  paths: string[];
  onChange: (paths: string[]) => void;
  disabled?: boolean;
  maxBytes?: number;
  maxCount?: number;
  onError?: (message: string) => void;
};

export function ManualEntryImagesField({
  siteId,
  paths,
  onChange,
  disabled = false,
  maxBytes = MANUAL_ENTRY_IMAGE_MAX_BYTES,
  maxCount = MANUAL_ENTRY_IMAGE_MAX_COUNT,
  onError,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { assertUploadAllowed } = useMediaStorage();
  const [uploadBusy, setUploadBusy] = useState(false);

  async function uploadFiles(files: FileList | null) {
    if (!files?.length) return;
    if (siteId < 1) {
      onError?.('请先选择站点');
      return;
    }
    if (!assertUploadAllowed()) return;
    const remain = maxCount - paths.length;
    if (remain < 1) {
      onError?.(`最多上传 ${maxCount} 张图片`);
      return;
    }
    setUploadBusy(true);
    const next: string[] = [];
    try {
      for (const file of Array.from(files).slice(0, remain)) {
        if (file.size > maxBytes) {
          const mb = Math.round(maxBytes / (1024 * 1024));
          throw new Error(`「${file.name}」超过单张 ${mb}MB 上限，请先压缩`);
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('site_id', String(siteId));
        const res = await fetch(apiUrl('upload_manual_entry_image'), {
          method: 'POST',
          body: fd,
          credentials: 'include',
        });
        const raw = await res.text();
        const text = raw.replace(/^\uFEFF/, '').trim();
        let parsed: { ok?: boolean; err?: string; local_path?: string };
        try {
          parsed = JSON.parse(text) as typeof parsed;
        } catch {
          throw new Error(text.slice(0, 120) || '上传响应无效');
        }
        if (parsed.err === 'unauthorized' && typeof window !== 'undefined') {
          window.location.replace(getSessionExpiredLoginUrl());
          return;
        }
        if (parsed.err === 'media_quota_exceeded') {
          dispatchMediaQuotaBlocked();
          return;
        }
        if (!parsed.ok || !parsed.local_path) {
          const err = parsed.err || '上传失败';
          throw new Error(err === 'file_too_large' ? `单张图片超过 ${Math.round(maxBytes / (1024 * 1024))}MB` : err);
        }
        next.push(parsed.local_path);
      }
      onChange([...paths, ...next]);
    } catch (e) {
      onError?.(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploadBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const busy = disabled || uploadBusy;
  const canAdd = paths.length < maxCount;

  return (
    <div>
      <span className={labelClass}>图片（可选）</span>
      <p className="mb-2 text-xs text-slate-500">
        支持本地上传，兼容 OSS 与本地存储；单张不超过 {Math.round(maxBytes / (1024 * 1024))}MB，最多 {maxCount} 张
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
        multiple
        className="sr-only"
        onChange={(e) => void uploadFiles(e.target.files)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy || !canAdd || siteId < 1}
          onClick={() => inputRef.current?.click()}
        >
          {uploadBusy ? '上传中…' : '选择图片'}
        </Button>
        <span className="text-xs text-slate-500">
          {paths.length}/{maxCount} 张
        </span>
      </div>
      {paths.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {paths.map((p) => (
            <li key={p} className="relative">
              <img
                src={resolveMediaUrl(p)}
                alt=""
                className="h-20 w-20 rounded-lg border border-slate-200 object-cover"
              />
              <button
                type="button"
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-xs text-white shadow hover:bg-rose-700 disabled:opacity-50"
                aria-label="移除图片"
                disabled={busy}
                onClick={() => onChange(paths.filter((x) => x !== p))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
